import * as React from 'react';
import {
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { PencilSimple as PencilSimpleIcon } from '@phosphor-icons/react';
import { ShieldWarning as ShieldWarningIcon } from '@phosphor-icons/react/dist/ssr/ShieldWarning';
import { UserCircle as UserIcon } from '@phosphor-icons/react/dist/ssr/UserCircle';
import { EnvelopeSimple as EnvelopeIcon } from '@phosphor-icons/react/dist/ssr';

import type { User } from '@/types/v2/user';
import { PropertyItem } from '@/components/core/v2/property-item';
import { PropertyList } from '@/components/core/v2/property-list';
import { CreateOrEditResidentModal } from '../../components/create-or-edit-resident-modal';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useQueryClient , useMutation } from '@tanstack/react-query';
import { useUser } from '@/hooks/v2/use-user';
import axios from '@/lib/v2/axios';
import { toast } from 'sonner';

interface ResidentDetailsProps {
  resident: User | null;
  isLoading: boolean;
  error: Error | null;
}

export function ResidentDetails({ resident, isLoading, error }: ResidentDetailsProps): React.JSX.Element {
  const dialog = useDialog();
  const queryClient = useQueryClient();
  const { community } = useUser();

  const resendInvitationMutation = useMutation({
    mutationFn: async (payload: { email: string; communityId: string | number }) => {
      const response = await axios.post('/api/v1/invitations/resend-community', {
        email: payload.email,
        community_id: payload.communityId,
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Invitation resent successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to resend invitation';
      toast.error(message);
    },
  });

  const handleEditSuccess = React.useCallback(
    (updatedUser: User) => {
      // Invalidate the resident query to refresh the data
      if (resident?.id) {
        queryClient.invalidateQueries({ queryKey: ['resident', resident.id] });
      }
      queryClient.invalidateQueries({ queryKey: ['residents'] });
    },
    [queryClient, resident?.id]
  );

  // Don't render anything if resident is null
  if (!resident) {
    return <></>;
  }

  return (
    <>
        <Stack spacing={4}>
          <Card>
            <CardHeader
              action={
                <Tooltip title="Edit User">
                  <IconButton onClick={dialog.handleOpen}>
                    <PencilSimpleIcon />
                  </IconButton>
                </Tooltip>
              }
              avatar={
                <Avatar>
                  <UserIcon fontSize="var(--Icon-fontSize)" />
                </Avatar>
              }
              title="User details"
            />
            <PropertyList divider={<Divider />} orientation="vertical" sx={{ '--PropertyItem-padding': '12px 24px' }}>
              {(
                [
                  { key: 'User ID', value: <Chip label={resident?.id} size="small" variant="soft" /> },
                  { key: 'Name', value: `${resident?.first_name} ${resident?.last_name}` },
                  { key: 'Email', value: resident?.email },
                  { key: 'Phone', value: resident?.phone || 'N/A' },
                  ...(resident?.email_verified_at === null ? [{
                    key: 'Status',
                    value: (
                      <Stack alignItems="center" direction="row" spacing={1}>
                        <Chip 
                          color="warning" 
                          label="Unverified" 
                          size="small" 
                          variant="outlined"
                        />
                        <Button
                          disabled={resendInvitationMutation.isPending}
                          onClick={() => { resendInvitationMutation.mutate({ 
                            email: resident.email || '', 
                            communityId: community?.id || '' 
                          }); }}
                          size="small"
                          startIcon={<EnvelopeIcon size={16} />}
                          sx={{ textTransform: 'none' }}
                          variant="outlined"
                        >
                          {resendInvitationMutation.isPending ? 'Resending...' : 'Resend Invitation'}
                        </Button>
                      </Stack>
                    )
                  }] : [])
                ] satisfies { key: string; value: React.ReactNode }[]
              ).map(
                (item): React.JSX.Element => (
                  <PropertyItem key={item.key} name={item.key} value={item.value} />
                )
              )}
            </PropertyList>
          </Card>
          
        </Stack>
        
        {/* Edit User Modal */}
        <CreateOrEditResidentModal
          communityId={community?.id}
          initialData={resident}
          mode="edit"
          onClose={dialog.handleClose}
          onSuccess={handleEditSuccess}
          open={dialog.open}
          userId={resident.id}
        />
      {/* <Grid lg={8} xs={12}>
    <Stack spacing={4}>
      <Payments
        ordersValue={2069.48}
        payments={[
          {
            currency: 'USD',
            amount: 500,
            invoiceId: 'INV-005',
            status: 'completed',
            createdAt: dayjs().subtract(5, 'minute').subtract(1, 'hour').toDate(),
          },
          {
            currency: 'USD',
            amount: 324.5,
            invoiceId: 'INV-004',
            status: 'refunded',
            createdAt: dayjs().subtract(21, 'minute').subtract(2, 'hour').toDate(),
          },
          {
            currency: 'USD',
            amount: 746.5,
            invoiceId: 'INV-003',
            status: 'completed',
            createdAt: dayjs().subtract(7, 'minute').subtract(3, 'hour').toDate(),
          },
          {
            currency: 'USD',
            amount: 56.89,
            invoiceId: 'INV-002',
            status: 'completed',
            createdAt: dayjs().subtract(48, 'minute').subtract(4, 'hour').toDate(),
          },
          {
            currency: 'USD',
            amount: 541.59,
            invoiceId: 'INV-001',
            status: 'completed',
            createdAt: dayjs().subtract(31, 'minute').subtract(5, 'hour').toDate(),
          },
        ]}
        refundsValue={324.5}
        totalOrders={5}
      />
      <Card>
        <CardHeader
          action={
            <Button color="secondary" startIcon={<PencilSimpleIcon />}>
              Edit
            </Button>
          }
          avatar={
            <Avatar>
              <CreditCardIcon fontSize="var(--Icon-fontSize)" />
            </Avatar>
          }
          title="Billing details"
        />
        <CardContent>
          <Card sx={{ borderRadius: 1 }} variant="outlined">
            <PropertyList divider={<Divider />} sx={{ '--PropertyItem-padding': '16px' }}>
              {(
                [
                  { key: 'Credit card', value: '**** 4142' },
                  { key: 'Country', value: 'United States' },
                  { key: 'State', value: 'Michigan' },
                  { key: 'City', value: 'Southfield' },
                  { key: 'Address', value: '1721 Bartlett Avenue, 48034' },
                  { key: 'Tax ID', value: 'EU87956621' },
                ] satisfies { key: string; value: React.ReactNode }[]
              ).map(
                (item): React.JSX.Element => (
                  <PropertyItem key={item.key} name={item.key} value={item.value} />
                )
              )}
            </PropertyList>
          </Card>
        </CardContent>
      </Card>
      <Card>
        <CardHeader
          action={
            <Button color="secondary" startIcon={<PlusIcon />}>
              Add
            </Button>
          }
          avatar={
            <Avatar>
              <HouseIcon fontSize="var(--Icon-fontSize)" />
            </Avatar>
          }
          title="Shipping addresses"
        />
        <CardContent>
          <Grid container spacing={3}>
            {(
              [
                {
                  id: 'ADR-001',
                  country: 'United States',
                  state: 'Michigan',
                  city: 'Lansing',
                  zipCode: '48933',
                  street: '480 Haven Lane',
                  primary: true,
                },
                {
                  id: 'ADR-002',
                  country: 'United States',
                  state: 'Missouri',
                  city: 'Springfield',
                  zipCode: '65804',
                  street: '4807 Lighthouse Drive',
                },
              ] satisfies Address[]
            ).map((address) => (
              <Grid key={address.id} md={6} xs={12}>
                <ShippingAddress address={address} />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
      <Notifications
        notifications={[
          {
            id: 'EV-002',
            type: 'Refund request approved',
            status: 'pending',
            createdAt: dayjs().subtract(34, 'minute').subtract(5, 'hour').subtract(3, 'day').toDate(),
          },
          {
            id: 'EV-001',
            type: 'Order confirmation',
            status: 'delivered',
            createdAt: dayjs().subtract(49, 'minute').subtract(11, 'hour').subtract(4, 'day').toDate(),
          },
        ]}
      />
    </Stack>
  </Grid> */}
  </>
  );
}
